import React, { useMemo, useState, useEffect } from 'react';
import { X, Search, Calendar, UtensilsCrossed } from 'lucide-react';
import { fmtDate } from '../lib/utils';

/**
 * Unified picker used from the Inbox reading pane ("Link to Event" /
 * "Link to Booking"). Shows a searchable list of events or table bookings
 * and calls `onPick(id)` when the user selects one.
 *
 * Props:
 *   isOpen    — boolean
 *   mode      — 'event' | 'booking'
 *   events, tableBookings — from AppContext
 *   onPick(id) — called with selected item's id
 *   onClose()  — close handler
 */
export default function LinkToItemModal({
  isOpen,
  mode = 'event',
  events = [],
  tableBookings = [],
  onPick,
  onClose,
}) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (isOpen) setQuery('');
  }, [isOpen]);

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (mode === 'event') {
      // Most recent / upcoming events first
      return [...events]
        .filter((e) => {
          if (!q) return true;
          return (
            (e.name || '').toLowerCase().includes(q) ||
            (e.client || '').toLowerCase().includes(q) ||
            (e.clientEmail || '').toLowerCase().includes(q) ||
            (e.clientPhone || '').toLowerCase().includes(q)
          );
        })
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
        .slice(0, 200);
    }
    return [...tableBookings]
      .filter((b) => {
        if (!q) return true;
        return (
          (b.guestName || '').toLowerCase().includes(q) ||
          (b.email || '').toLowerCase().includes(q) ||
          (b.phone || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.time || '').localeCompare(a.time || ''))
      .slice(0, 200);
  }, [mode, events, tableBookings, query]);

  if (!isOpen) return null;

  const title = mode === 'event' ? 'Link email to an event' : 'Link email to a table booking';
  const Icon = mode === 'event' ? Calendar : UtensilsCrossed;
  const placeholder = mode === 'event'
    ? 'Search by event name, client, email, or phone…'
    : 'Search by guest name, email, or phone…';

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[90] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Icon size={18} className="text-emerald-600" />
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-700 rounded"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-5 py-3 border-b border-slate-200">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              autoFocus
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-slate-400 italic p-6 text-center">
              No {mode === 'event' ? 'events' : 'table bookings'} matched. Try a broader search.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((it) => (
                <li key={it.id}>
                  <button
                    type="button"
                    onClick={() => onPick?.(it.id)}
                    className="w-full text-left px-5 py-3 hover:bg-slate-50 transition"
                  >
                    {mode === 'event' ? (
                      <>
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="font-medium text-slate-900 truncate">{it.name || '(unnamed event)'}</p>
                          <span className="text-xs text-slate-500 whitespace-nowrap">{it.date ? fmtDate(it.date) : ''}</span>
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5">
                          {[it.client, it.clientEmail].filter(Boolean).join(' · ') || 'No client info'}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="font-medium text-slate-900 truncate">{it.guestName || '(unnamed booking)'}</p>
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            {it.date ? fmtDate(it.date) : ''}{it.time ? ` · ${it.time}` : ''}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5">
                          {[it.covers ? `${it.covers} covers` : null, it.venue, it.email].filter(Boolean).join(' · ')}
                        </p>
                      </>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
